import { Controller } from "@hotwired/stimulus";

type HtmxRequestEvent = Event & {
    detail?: {
        elt?: Element;
        requestConfig?: {
            path?: string;
        };
        target?: Element;
    };
};

const marksInFlight = new Set<string>();

export default class extends Controller<HTMLElement> {
    static values = {
        markUrl: String,
        revision: String,
    };

    declare readonly markUrlValue: string;
    declare readonly hasMarkUrlValue: boolean;

    private marked = false;
    private connected = false;
    private readonly handleBeforeRequest = (event: Event): void => {
        const detail = (event as HtmxRequestEvent).detail;
        const path = detail?.requestConfig?.path || "";

        if (!path || !this.requestStartsInsideTeamChat(detail?.elt) || this.isTeamChatRequest(path)) {
            return;
        }

        this.markRead();
    };
    private readonly handleBeforeSwap = (event: Event): void => {
        const detail = (event as HtmxRequestEvent).detail;
        const path = detail?.requestConfig?.path || "";

        if (!path || detail?.target !== this.element || this.isTeamChatRequest(path)) {
            return;
        }

        this.markRead();
    };
    private readonly handlePageHide = (): void => {
        this.markRead();
    };

    connect(): void {
        this.connected = true;
        document.body.addEventListener("htmx:beforeRequest", this.handleBeforeRequest);
        document.body.addEventListener("htmx:beforeSwap", this.handleBeforeSwap);
        window.addEventListener("pagehide", this.handlePageHide);
        this.markRead();
    }

    disconnect(): void {
        this.connected = false;
        document.body.removeEventListener("htmx:beforeRequest", this.handleBeforeRequest);
        document.body.removeEventListener("htmx:beforeSwap", this.handleBeforeSwap);
        window.removeEventListener("pagehide", this.handlePageHide);
    }

    markUrlValueChanged(): void {
        // Morphdom keeps this controller when switching tickets.
        this.marked = false;
        this.markRead();
    }

    revisionValueChanged(): void {
        if (!this.connected) return;
        // A reply or run completion rendered on this ticket has already been seen.
        this.marked = false;
        this.markRead();
    }

    private markRead(): void {
        if (this.marked || !this.hasMarkUrlValue || !this.markUrlValue) {
            return;
        }

        const markUrl = this.markUrlValue;
        if (marksInFlight.has(markUrl)) {
            this.marked = true;
            return;
        }

        this.marked = true;
        marksInFlight.add(markUrl);

        void fetch(markUrl, {
            method: "POST",
            credentials: "same-origin",
            keepalive: true,
        })
            .then((response) => {
                if (!response.ok) {
                    this.marked = false;
                    return;
                }

                const ticketId = new URL(markUrl, window.location.origin).searchParams.get("ticketId");
                if (ticketId) {
                    clearTeamChatUnread(ticketId);
                    document.body.dispatchEvent(
                        new CustomEvent("ticket-team-chat-read", {
                            bubbles: true,
                            detail: { ticketId },
                        })
                    );
                }
            })
            .catch(() => {
                this.marked = false;
            })
            .finally(() => {
                marksInFlight.delete(markUrl);
            });
    }

    private isTeamChatRequest(path: string): boolean {
        if (!path) {
            return false;
        }

        let url: URL;

        try {
            url = new URL(path, window.location.origin);
        } catch (_error) {
            return false;
        }

        return url.pathname === "/TicketTeamChatFragment" || url.pathname === "/CreateTicketTeamChatMessage";
    }

    private requestStartsInsideTeamChat(element: Element | undefined): boolean {
        return element instanceof Element && this.element.contains(element);
    }
}

function clearTeamChatUnread(ticketId: string): void {
    const row = document.querySelector(`[data-testid="ticket-list-row"][data-ticket-id="${ticketId}"]`);
    row?.querySelectorAll('[data-testid="ticket-ping-preview"], [data-testid="ticket-unread-badge"]').forEach((badge) => {
        badge.remove();
    });
    document.querySelectorAll('#tickets-chat-fragment [data-testid="ticket-team-chat-unread-badge"]').forEach((badge) => {
        badge.remove();
    });
}
